import React, { useState } from "react";
import {
  Row,
  Col,
  Divider,
  Button,
  Space,
  Modal,
  Form,
  notification,
} from "antd";
import styles from "./Payment.module.scss";
import commonStyles from "../Subscription/YourPlan/YourPlan.module.scss";
import {
  pencilIcon,
  questionMark,
  trashCanIcon,
  paymentEditIcon,
} from "../../../assets/images";
import { useHistory } from "react-router-dom";
import CustomButton from "../../../common/Buttons/CustomButton";
import CustomTooltip from "../../../common/Tooltips/CustomTooltip";
import { usePaymentDetailsCtx } from "../../../context/PaymentDetailsContext";
import getCreditCardKindIcon from "../../../helpers/getCreditCardKindIcon";
import moment from "moment";
import { usePlanDetailsCtx } from "../../../context/PlanDetailsContext";
import CreditCardForm from "../../Shared/CreditCardForm/CreditCardForm";
import { useCreditCardActions } from "../../../hooks/useCreditCardActions";
import { TrackAnalytics } from "../../../types/ApplicationTypes";
import buttonStyles from "../../Shared/Styles/ButtonStyles.module.scss";
import deleteIcon from "../../../assets/icons/deleteFilled.svg";

const Payment = () => {
  const [isOpenCreditCardModal, setIsOpenCreditCardModal] = useState<boolean>(
    false
  );
  const [isRemoveCreditCardModal, setIsRemoveCreditCardModal] = useState<
    boolean
  >(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const history = useHistory();

  const [form] = Form.useForm();

  const {
    isValidCCState,
    saveCreditCard,
    deleteCreditCard,
    validateAndCheckForm,
    creditCardFormRef,
    shouldDisableButton,
    resetFormAndSetDefaults,
  } = useCreditCardActions(form);

  const {
    defaultSavedCreditCardDetails: creditCard,
    isDefaultSavedCreditCardExists,
    getDefaultSavedCreditCard,
  } = usePaymentDetailsCtx();

  const { currentPlan } = usePlanDetailsCtx();

  const handleBillingHistory = () => {
    history.push("/DashBoard/BillingHistory");
    TrackAnalytics(
      "/DashBoard/BillingHistory",
      "Visit billing history",
      "Billing History Page"
    );
  };

  const handleOpenCreditCard = () => setIsOpenCreditCardModal(true);

  const handleDeleteCreditCard = () => setIsRemoveCreditCardModal(true);

  const handleRemoveCreditCard = async () => {
    try {
      await deleteCreditCard(creditCard.paymentInstrumentId);
      await getDefaultSavedCreditCard();
      setIsRemoveCreditCardModal(false);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to Delete",
      });
    }
  };

  const handleSaveCreditCard = async () => {
    try {
      setIsLoading(true);
      await saveCreditCard(true);
      await getDefaultSavedCreditCard();
      resetFormAndSetDefaults();
      setIsOpenCreditCardModal(false);
    } catch (error) {
      // We are already showing an error on the form. so removed toast notification.
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (modalBtn: string) => {
    if (modalBtn === "AddEditCreditCard") {
      resetFormAndSetDefaults();
      setIsOpenCreditCardModal(false);
    } else if (modalBtn === "NoTakeMeBack") {
      setIsRemoveCreditCardModal(false);
    }
  };

  return (
    <Row className={styles.paymentContainer} justify="center">
      <Col span={24}>
        <Row justify="space-between" align="middle" gutter={[0, 15]}>
          <Col span={24} className={styles.paymentLabel}>
            Payment
          </Col>
          {isDefaultSavedCreditCardExists ? (
            <>
              <Col className={styles.creditCardDetails}>
                <Row align="middle" gutter={[10, 0]}>
                  <Col className={styles.savedCardLabel}>Saved Card</Col>
                </Row>
                <Row align="middle" gutter={[10, 0]}>
                  <Col>{getCreditCardKindIcon(creditCard?.kind)}</Col>
                  <Col>
                    <Row className={styles.cardNumber}>
                      **** {creditCard?.last4}
                    </Row>
                    <Row className={styles.expiryDate}>
                      Expires {moment(creditCard?.expiresOn).format("MM/YYYY")}
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col className={styles.creditCardActions}>
                <Row justify="end" gutter={[10, 0]}>
                  {currentPlan === "Basic" ? (
                    <Col>
                      <Button
                        className={buttonStyles.iconBtn}
                        onClick={handleDeleteCreditCard}
                      >
                        <img
                          className={styles.deleteIconStyle}
                          src={deleteIcon}
                          alt="paymentDelete"
                        />
                      </Button>
                    </Col>
                  ) : (
                    ""
                  )}
                  <Col>
                    <Button
                      className={buttonStyles.iconBtn}
                      onClick={handleOpenCreditCard}
                    >
                      <img src={paymentEditIcon} alt="paymentEdit" />
                    </Button>
                  </Col>
                </Row>
              </Col>
            </>
          ) : (
            <>
              <Col className={styles.paymentBasicContainer}>
                <Row>
                  <Col className={styles.noCreditCardText}>
                    No credit card added.
                  </Col>
                </Row>

                <Col className={styles.addCardBtnContainer}>
                  <Button
                    type="link"
                    className={styles.addCardBtn}
                    onClick={handleOpenCreditCard}
                  >
                    Add Card
                  </Button>
                </Col>
              </Col>
            </>
          )}
        </Row>
      </Col>

      {/* TO_DO: Wanda Cash for Phase-2 */}

      {/* <Divider className={styles.paymentDivider} dashed />

      <Col span={24}>
        <Row justify="space-between" align="middle">
          <Col className={styles.wandaCashText}>
            <Space>
              Wanda Cash
              <CustomTooltip
                tooltipText="Use your Wanda Cash to pay for services."
                className="customTooltip"
                icon={questionMark}
              />
            </Space>
          </Col>
          <Col className={styles.availableCashText}>$0</Col>
        </Row>
      </Col> */}

      <Divider className={styles.paymentDivider} dashed />

      <Col span={24}>
        <Button
          type="link"
          id={styles.billingBtn}
          onClick={handleBillingHistory}
        >
          View billing history
        </Button>
      </Col>

      <Modal  
        className={styles.addEditCreditCardModal}
        visible={isOpenCreditCardModal}
        centered={true}
        width="unset"
        footer={
          <Row
            justify="center"
            gutter={[10, 10]}
            className={`${styles.commonPaymentModalBtns} ${styles.footerMargin}`}
          >
            <Col xs={24} lg={0} />
            <Col>
              <Button
                className={`${styles.modalWhiteBtn} ${styles.cancelBtnWidth}`}
                onClick={() => handleClose("AddEditCreditCard")}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                className={`${styles.modalBlueBtn} ${isLoading &&
                  styles.autoWidth} ${shouldDisableButton &&
                  commonStyles.disableBtnOpacity}`}
                onClick={handleSaveCreditCard}
                disabled={shouldDisableButton}
                loading={isLoading}
              >
                Save Credit Card
              </Button>
            </Col>
          </Row>
        }
      >
        <Row justify="center" gutter={[0, 50]}>
          <Col span={24} className={styles.modalTitle}>
            Credit Card Details
          </Col>
          <Col span={24} />
        </Row>

        <CreditCardForm
          form={form}
          ref={creditCardFormRef}
          isModalLayout={true}
          isValidCCState={isValidCCState}
          validateAndCheckForm={validateAndCheckForm}
        />
      </Modal>

      <Modal
        className={styles.removeCreditCardModal}
        visible={isRemoveCreditCardModal}
        centered={true}
        width="unset"
        footer={
          <Row
            justify="center"
            gutter={[15, 10]}
            className={styles.commonPaymentModalBtns}
          >
            <Col>
              <Button
                className={styles.modalWhiteBtn}
                onClick={() => handleClose("NoTakeMeBack")}
              >
                No, Take Me Back
              </Button>
            </Col>
            <Col>
              <Button
                className={styles.modalBlueBtn}
                onClick={handleRemoveCreditCard}
              >
                Yes, Remove
              </Button>
            </Col>
          </Row>
        }
      >
        <Row justify="center" gutter={[0, 25]}>
          <Col className={styles.modalTitle}>Remove Credit Card</Col>
          <Col className={styles.modalContent}>
            Are you sure that you want to remove your saved credit card (ending
            in {creditCard?.last4})?
          </Col>
          <Col span={24} />
        </Row>
      </Modal>
    </Row>
  );
};

export default Payment;
